import React, { useState } from "react";
import Layout from "../components/layout/Layout";
import { Send, Phone, Mail, MapPin, Clock } from "lucide-react";
import CTASection from "../components/common/CTASection";
import { useWhatsAppForm } from "../hooks/useWhatsAppForm";
import hero from "../assets/home-section.jpg";

const ContactPage = () => {
  const { sendToWhatsApp, isSubmitting, setIsSubmitting } = useWhatsAppForm();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    sendToWhatsApp(formData);
    setTimeout(() => setIsSubmitting(false), 2000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Layout>
      {/* ===== HERO SECTION ===== */}
      <section
        className="relative py-16 sm:py-20 bg-fixed bg-cover bg-center"
        style={{
          backgroundImage: `url(${hero})`,
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-[#2F3C7E]/70"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
            Contact Us
          </h1>
          <p className="text-lg sm:text-xl text-[#E6B325]">
            Get in Touch With Us
          </p>
        </div>
      </section>

      {/* ===== CONTACT FORM SECTION ===== */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="text-center mb-8 sm:mb-10 md:mb-12">
                <span className="text-sm sm:text-base md:text-lg text-gray-600 block mb-2 sm:mb-3">
                  Complete the form and we'll respond promptly
                </span>
                <h4 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2F3C7E]">
                  How can we assist you?
                </h4>
              </div>

              {/* Contact Form */}
              <form className="space-y-5 sm:space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  {/* Left Column */}
                  <div className="space-y-4 sm:space-y-6">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your full name"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#E6B325] transition text-sm sm:text-base"
                      required
                      value={formData.name}
                      onChange={handleChange}
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Your email address"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#E6B325] transition text-sm sm:text-base"
                      required
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Your phone number"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#E6B325] transition text-sm sm:text-base"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Right Column - Message */}
                  <div>
                    <textarea
                      name="message"
                      placeholder="Tell us about your requirements"
                      rows="6"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#E6B325] transition h-full text-sm sm:text-base"
                      required
                      value={formData.message}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                </div>

                {/* Submit Button - Centered on mobile, left-aligned on desktop */}
                <div className="text-center md:text-left">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#2F3C7E] text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-[#1E2A5A] transition shadow-md hover:shadow-lg transform hover:-translate-y-1 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 text-sm sm:text-base mx-auto md:mx-0"
                  >
                    {isSubmitting ? "Opening WhatsApp..." : "Send Message"}
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ===== OFFICE INFORMATION SECTION ===== */}
      <section className="py-12 sm:py-14 md:py-16 bg-blue-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 justify-center">
            {/* Corporate Office */}
            <div className="text-center mx-auto max-w-sm">
              <div className="flex justify-center md:justify-center mb-3">
                <MapPin className="w-6 h-6 text-[#E6B325]" />
              </div>
              <h6 className="text-base sm:text-lg font-bold text-[#2F3C7E] mb-2 sm:mb-3">
                Corporate Office
              </h6>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                Krishna Restaurant Dharampur Road,
                <br />
                Atak Pardi, Valsad - 396007,
                <br />
                (beside Keshav Kunj, Near Atakpardi)
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mb-2">
                <Phone className="w-3 h-3 inline mr-1 text-[#E6B325]" />
                <a
                  href="tel:+916377733291"
                  className="text-[#2F3C7E] hover:text-[#E6B325] transition"
                >
                  +91 6377733291
                </a>
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                <Mail className="w-3 h-3 inline mr-1 text-[#E6B325]" />
                <a
                  href="mailto:info@shivshaktihospitality.com"
                  className="text-[#2F3C7E] hover:text-[#E6B325] transition"
                >
                  info@shivshaktihospitality.com
                </a>
              </p>
              <a
                href="https://maps.app.goo.gl/ccbqVk8PNWCGnJyo7"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm font-semibold text-[#2F3C7E] uppercase tracking-wider border-b-2 border-[#E6B325] pb-1 hover:text-[#E6B325] transition"
              >
                View on Google Map
              </a>
            </div>

            {/* Key Contacts */}
            <div className="text-center mx-auto max-w-sm">
              <div className="flex justify-center md:justify-center mb-3">
                <Phone className="w-6 h-6 text-[#E6B325]" />
              </div>
              <h6 className="text-base sm:text-lg font-bold text-[#2F3C7E] mb-2 sm:mb-3">
                Key Contacts
              </h6>

              <div className="mb-3">
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-semibold text-[#2F3C7E]">
                    Mr. Jaspal Singh
                  </span>{" "}
                  - Director
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  <a
                    href="tel:+916377733291"
                    className="text-[#2F3C7E] hover:text-[#E6B325] transition"
                  >
                    +91 6377733291
                  </a>
                  ,{" "}
                  <a
                    href="mailto:info@shivshaktihospitality.com"
                    className="text-[#2F3C7E] hover:text-[#E6B325] transition"
                  >
                    info@shivshaktihospitality.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MAP SECTION ===== */}
      <section className="w-full">
        <div className="relative h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] w-full">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3734.927616225241!2d72.94984397518782!3d20.591013402669798!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be0c267361f85fb%3A0x4b690c9515a90df8!2sHotel%20Krishna%20dharmpur%20road%20valsad!5e0!3m2!1sen!2sin!4v1773988953237!5m2!1sen!2sin"
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Krishna Restaurant Location"
          ></iframe>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <CTASection />
    </Layout>
  );
};

export default ContactPage;
